import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';
import type { TransformedNotificationItem, TransformedNotificationsFeedResponse } from '../components/notifications/utils/EventTypes';
import { Event } from '../components/notifications/utils/EventTypes';

import { openNotification } from '../components';
import { ApiNotificationsRepository } from '../services/notifications/ApiNotificationsRepository';
import type { ValidationResult } from '../services/notifications/feedValidation';
import type { NotificationLastKey } from '../services/notifications/types';
import { QueryKeys } from '../utils/queryKeys';
import { trackError } from '@proposify/frontend-observability';
import { useTranslation } from 'react-i18next';

/**
 * Transformed notification for UI rendering.
 *
 * The validation layer guarantees that type and documentId are present
 * for all items that reach the UI layer.
 */

const normalizeCommentedNotificationData = (data: Record<string, unknown>) => ({
  ...data,
  author: typeof data.author === 'string' ? data.author : '',
  documentName: typeof data.documentName === 'string' ? data.documentName : '',
  commentText: typeof data.commentText === 'string' ? data.commentText : '',
  threadId: typeof data.threadId === 'string' ? data.threadId : '',
  previewUrl: typeof data.previewUrl === 'string' ? data.previewUrl : '',
});

const normalizeViewedNotificationData = (data: Record<string, unknown>) => ({
  ...data,
  viewerName: typeof data.viewerName === 'string' ? data.viewerName : '',
  companyName: typeof data.companyName === 'string' ? data.companyName : '',
  documentName: typeof data.documentName === 'string' ? data.documentName : '',
});

export const useNotificationFeedQuery = (userId: string | null, params?: { lastKey?: NotificationLastKey; limit?: number }) => {
  const queryResult = useQuery<ValidationResult, Error, TransformedNotificationsFeedResponse>({
    queryKey: QueryKeys.getNotificationsFeedByUserId(userId, params),
    queryFn: () => ApiNotificationsRepository.getNotificationsFeedByUserId({ userId: userId!, params }),
    select: (data) => {
      return {
        items: data.items.map((item): TransformedNotificationItem => {
          const base = {
            type: item.type,
            viewed: item.viewed,
            createdAt: dayjs(item.createdAt),
            notificationId: item.notificationId,
            documentId: item.documentId,
          };
          const rawData = item.data as Record<string, unknown>;
          let normalizedData = rawData;

          if (item.type === Event.COMMENTED) {
            normalizedData = normalizeCommentedNotificationData(rawData);
          }

          if (item.type === Event.VIEWED) {
            normalizedData = normalizeViewedNotificationData(rawData);
          }

          return { ...normalizedData, ...base } as TransformedNotificationItem;
        }),
        lastKey: data.lastKey,
      };
    },
    enabled: !!userId,
  });

  const getNotificationFeed = async () => {
    if (!userId) {
      throw new Error('User ID is required');
    }
    return await ApiNotificationsRepository.getNotificationsFeedByUserId({ userId, params });
  };

  return {
    ...queryResult,
    getNotificationFeed,
  };
};

export const useMarkNotificationAsViewed = (userId: string | null) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const { mutate } = useMutation({
    mutationFn: (notificationId: string) => ApiNotificationsRepository.markNotificationAsRead(notificationId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: QueryKeys.getNotificationsFeedByUserIdBase(userId),
      });
    },
    onError: (error) => {
      trackError(error);
      openNotification({
        title: t('error'),
        type: 'error',
      });
    },
  });

  return { markNotificationAsViewed: mutate };
};
